/**
 * NotificationsDrawer — bildirim listesi modal/drawer.
 *
 * Sprint 4 UI: shadcn Card + Badge + lucide-react ikonları.
 * - Liste (en yeni üstte)
 * - Okundu / Tümünü okundu işaretle
 * - Boş state
 */

import { Bell, Check, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useUnreadNotificationsCount,
  type NotificationItem,
} from '@/queries/notifications'

interface NotificationsDrawerProps {
  open: boolean
  onClose: () => void
}

export function NotificationsDrawer({ open, onClose }: NotificationsDrawerProps) {
  const { data, isLoading } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const { data: unreadCount = 0 } = useUnreadNotificationsCount()

  if (!open) return null

  const items = data?.items ?? []
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end bg-foreground/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Bildirimler"
    >
      <aside
        className="flex h-full w-full max-w-md flex-col border-l border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Bildirimler</h2>
            {unreadCount > 0 && <Badge variant="default">{unreadCount}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                <Check className="mr-1 h-4 w-4" />
                Tümünü okundu işaretle
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Kapat">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Bell className="h-8 w-8" />}
              title="Henüz bildirim yok"
              description="Yeni teklif, sipariş veya mesaj geldiğinde burada görünür."
            />
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <NotificationRow key={n.id} item={n} onMarkRead={() => markRead.mutate(n.id)} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  )
}

function NotificationRow({ item, onMarkRead }: { item: NotificationItem; onMarkRead: () => void }) {
  const isUnread = !item.readAt
  return (
    <li
      className={cn(
        'flex flex-col gap-1 p-4 transition-colors hover:bg-muted/50',
        isUnread && 'bg-primary/5',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={cn(
            'text-sm font-medium',
            isUnread ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {item.title}
        </p>
        {isUnread && (
          <button
            type="button"
            onClick={onMarkRead}
            aria-label="Okundu işaretle"
            className="text-xs text-primary hover:underline"
          >
            Okundu
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{item.body}</p>
      {item.link && (
        <a href={item.link} className="text-xs text-primary hover:underline">
          Görüntüle →
        </a>
      )}
      <time className="text-xs text-muted-foreground">
        {new Date(item.createdAt).toLocaleString('tr-TR')}
      </time>
    </li>
  )
}
